import { useTranslation } from 'react-i18next';
import { CalendarEventChip } from '@/pages/calendar/CalendarEventChip';
import { sortDayEvents } from '@/lib/calendarDate';
import { formatWeekdayDayMonth } from '@/lib/format';
import type { CalendarEvent } from '@/types/shared';
import './CalendarAgenda.css';

interface CalendarAgendaProps {
  /** The full visible-grid day list, ordered (T104 §4.3). */
  days: string[];
  eventsByDate: Map<string, CalendarEvent[]>;
  todayStr: string;
  language: string;
  /** When the whole window has zero events, the shared empty note (rendered once by
   * `CalendarPage`) already covers both the grid and this agenda — render nothing here rather
   * than an empty list with no explanation (T104 §4.3). */
  isEmpty: boolean;
}

/**
 * T104 §4.3 mobile agenda fallback (`< bp.md`, hidden above it via CSS — see `CalendarGrid.css`'s
 * matching `display: none` above `bp.md`). Only days with ≥1 event get a section, EXCEPT a
 * "Today" anchor which always renders (when today is in the visible window) so there's always a
 * "you are here" landmark, even with 0 events that day.
 */
export function CalendarAgenda({
  days,
  eventsByDate,
  todayStr,
  language,
  isEmpty,
}: CalendarAgendaProps) {
  const { t } = useTranslation();

  if (isEmpty) return null;

  const relevantDays = days.filter(
    (dateStr) => (eventsByDate.get(dateStr)?.length ?? 0) > 0 || dateStr === todayStr,
  );

  return (
    <div className="fa-cal-agenda">
      {relevantDays.map((dateStr) => {
        const dayEvents = sortDayEvents(eventsByDate.get(dateStr) ?? []);
        const isToday = dateStr === todayStr;

        return (
          <section
            key={dateStr}
            className="fa-cal-agenda__day"
            aria-labelledby={`fa-cal-agenda-h-${dateStr}`}
          >
            <h2 id={`fa-cal-agenda-h-${dateStr}`} className="fa-cal-agenda__day-heading">
              {formatWeekdayDayMonth(dateStr, language)}
              {isToday && (
                <span className="fa-cal-agenda__today-badge">{t('calendar.nav.today')}</span>
              )}
            </h2>
            {dayEvents.length === 0 ? (
              <p className="fa-cal-agenda__no-events">{t('calendar.states.agendaNoEventsToday')}</p>
            ) : (
              <ul className="fa-cal-agenda__list">
                {dayEvents.map((event) => (
                  <li key={event.id} className="fa-cal-agenda__item">
                    <CalendarEventChip event={event} language={language} variant="expanded" />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
